<template>
  <div class="hcp-tag-select" v-clickoutside="closeDropdown">
    <div class="tag-input" :class="{ open: dropdownVisible }" @click="openDropdown">
      <span
        v-for="tag in selectedTags"
        :key="tag.id"
        class="hcp-tag-chip"
        :class="tag.color"
        @click.stop="removeTag(tag.id)"
      >
        {{ tag.name }} ×
      </span>
      <span v-if="!selectedTags.length" class="placeholder">Please select tag</span>
      <i class="el-icon-arrow-down"></i>
    </div>
    <div v-show="dropdownVisible" class="tag-dropdown" :style="dropdownStyle">
      <div class="drop-body">
        <div class="level1">
          <div
            v-for="category in categories"
            :key="category.id"
            class="level1-row"
            :class="{ active: category.id === activeCategoryId }"
            @click.stop="activeCategoryId = category.id"
          >
            {{ category.name }}
          </div>
        </div>
        <div class="level2">
          <div class="level2-head">
            <span>{{ activeCategory.name }}</span>
            <small>{{ selectedCountInCategory }} selected</small>
          </div>
          <div class="level2-list">
            <div
              v-for="value in activeCategory.values"
              :key="value.id"
              class="option"
              :class="{ checked: isSelected(value.id) }"
              @click.stop="toggleTag(value.id)"
            >
              <span class="box"></span>
              {{ value.name }}
            </div>
          </div>
        </div>
      </div>
      <div class="drop-actions">
        <el-button type="text" @click.stop="clearAll">Clear</el-button>
        <el-button type="primary" size="mini" @click.stop="closeDropdown">Done</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import Clickoutside from "element-ui/lib/utils/clickoutside";
import { loadHcpTagCatalog, resolveHcpTags, tagCatalog } from "@/utils/hcp-tag-catalog";

export default {
  name: "HcpTagSelect",
  directives: { Clickoutside },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    bindOwner: {
      type: Boolean,
      default: false,
    },
    ownerUserId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      activeCategoryId: "",
      dropdownVisible: false,
      dropdownStyle: {},
    };
  },
  computed: {
    categories() {
      return tagCatalog.categories || [];
    },
    selectedTags() {
      return resolveHcpTags(this.value);
    },
    activeCategory() {
      return this.categories.find((item) => item.id === this.activeCategoryId) || this.categories[0] || { name: "", values: [] };
    },
    selectedCountInCategory() {
      return (this.activeCategory.values || []).filter((item) => this.isSelected(item.id)).length;
    },
  },
  watch: {
    ownerUserId(value, oldValue) {
      if (this.bindOwner && value !== oldValue) this.refreshCatalog();
    },
  },
  created() {
    this.refreshCatalog();
  },
  methods: {
    refreshCatalog() {
      const request = this.bindOwner
        ? loadHcpTagCatalog(this.axios, this.ownerUserId)
        : loadHcpTagCatalog(this.axios);
      request.then(() => {
        if (!this.categories.some((item) => item.id === this.activeCategoryId) && this.categories[0]) {
          this.activeCategoryId = this.categories[0].id;
        }
      }).catch(() => {});
    },
    isSelected(id) {
      return this.value.includes(id);
    },
    openDropdown() {
      this.placeDropdown();
      this.dropdownVisible = true;
      window.addEventListener("scroll", this.placeDropdown, true);
      window.addEventListener("resize", this.placeDropdown);
    },
    closeDropdown() {
      this.dropdownVisible = false;
      window.removeEventListener("scroll", this.placeDropdown, true);
      window.removeEventListener("resize", this.placeDropdown);
    },
    placeDropdown() {
      const input = this.$el && this.$el.querySelector(".tag-input");
      if (!input) return;
      const rect = input.getBoundingClientRect();
      const width = Math.min(460, window.innerWidth - 16);
      const panelHeight = 332;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      let top = rect.bottom + 4;
      if (top + panelHeight > window.innerHeight - 8 && rect.top > panelHeight) {
        top = rect.top - panelHeight - 4;
      }
      this.dropdownStyle = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        zIndex: 3000,
      };
    },
    toggleTag(id) {
      const next = this.value.includes(id)
        ? this.value.filter((item) => item !== id)
        : this.value.concat(id);
      this.$emit("input", next);
    },
    removeTag(id) {
      this.$emit("input", this.value.filter((item) => item !== id));
    },
    clearAll() {
      this.$emit("input", []);
    },
  },
  beforeDestroy() {
    this.closeDropdown();
  },
};
</script>

<style lang="scss" scoped>
.hcp-tag-select {
  position: relative;
  width: 100%;
}

.tag-input {
  min-height: 32px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 4px 24px 4px 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  cursor: pointer;
  background: #fff;
  position: relative;

  &.open,
  &:hover {
    border-color: #c0c4cc;
  }

  .el-icon-arrow-down {
    position: absolute;
    right: 8px;
    top: 9px;
    color: #c0c4cc;
    font-size: 12px;
  }

  .placeholder {
    color: #c0c4cc;
    font-size: 12px;
    padding-left: 4px;
  }
}

.hcp-tag-chip {
  display: inline-block;
  height: 20px;
  line-height: 18px;
  border: 1px solid;
  border-radius: 3px;
  padding: 0 6px;
  font-size: 11px;
  white-space: nowrap;

  &.red {
    background: #fff4f3;
    border-color: #f2aaa4;
    color: #d52b1e;
  }

  &.blue {
    background: #eef9fc;
    border-color: #9ed3e3;
    color: #0078a3;
  }

  &.green {
    background: #f0f9eb;
    border-color: #b3e19d;
    color: #4c9f28;
  }

  &.orange {
    background: #fdf6ec;
    border-color: #f3d19e;
    color: #c27e18;
  }
}

.tag-dropdown {
  background: #fff;
  border: 1px solid #dfe3ea;
  border-radius: 6px;
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.16);
  display: flex;
  flex-direction: column;
  z-index: 20;
  overflow: hidden;
}

.drop-body {
  display: flex;
  min-height: 200px;
  max-height: 280px;
}

.level1 {
  width: 176px;
  flex-shrink: 0;
  background: #fafbfc;
  border-right: 1px solid #ebeef5;
  padding: 8px 0;
  overflow-y: auto;
}

.level1-row {
  min-height: 44px;
  padding: 10px 28px 10px 14px;
  font-size: 12px;
  line-height: 1.35;
  color: #606266;
  border-left: 3px solid transparent;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  word-break: keep-all;

  &:after {
    content: "›";
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #b9c0ca;
  }

  &.active {
    background: #fff;
    color: #d52b1e;
    font-weight: 600;
    border-left-color: #d52b1e;
  }
}

.level2 {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 14px 4px;
}

.level2-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 8px;
  margin-bottom: 2px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;

  span {
    min-width: 0;
    line-height: 1.35;
  }

  small {
    flex-shrink: 0;
    color: #d52b1e;
    font-weight: 400;
    white-space: nowrap;
  }
}

.level2-list {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 8px;
}

.option {
  min-height: 36px;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #606266;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;

  .box {
    width: 14px;
    height: 14px;
    border: 1px solid #c7cdd5;
    border-radius: 3px;
    margin-right: 8px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &.checked {
    color: #d52b1e;

    .box {
      background: #d52b1e;
      border-color: #d52b1e;

      &:after {
        content: "✓";
        color: #fff;
        font-size: 10px;
        line-height: 1;
      }
    }
  }
}

.drop-actions {
  flex-shrink: 0;
  border-top: 1px solid #ebeef5;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff;
}
</style>
